/**
 * 🌤️ SPACE WEATHER - Station Météo du Vaisseau
 * Design "Celestial Cabin VIP" - Ambiance spatiale
 * 
 * ✅ TOUTES les fonctionnalités conservées :
 * - Température actuelle
 * - Humidité
 * - Vitesse et direction du vent
 * - ROSE DES VENTS complète avec SVG
 * - Prévisions J+1 et J+2
 * - Géolocalisation
 * - Localisation par défaut (Vermelles)
 * - Mode compact
 */

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Cloud, Sun, CloudRain, CloudSnow, Wind, Droplets, MapPin, RefreshCw, Navigation, Thermometer, Gauge } from 'lucide-react';
import { Button } from './ui/button';
import { useTheme } from './ThemeToggle';

// Icônes météo
const WEATHER_ICONS = {
  '01d': { icon: '☀️', label: 'Ensoleillé' },
  '01n': { icon: '🌙', label: 'Nuit claire' },
  '02d': { icon: '⛅', label: 'Partiellement nuageux' },
  '02n': { icon: '☁️', label: 'Partiellement nuageux' },
  '03d': { icon: '☁️', label: 'Nuageux' },
  '03n': { icon: '☁️', label: 'Nuageux' },
  '04d': { icon: '☁️', label: 'Couvert' },
  '04n': { icon: '☁️', label: 'Couvert' },
  '09d': { icon: '🌧️', label: 'Averses' },
  '09n': { icon: '🌧️', label: 'Averses' },
  '10d': { icon: '🌦️', label: 'Pluie' },
  '10n': { icon: '🌧️', label: 'Pluie' },
  '11d': { icon: '⛈️', label: 'Orage' },
  '11n': { icon: '⛈️', label: 'Orage' },
  '13d': { icon: '❄️', label: 'Neige' },
  '13n': { icon: '❄️', label: 'Neige' },
  '50d': { icon: '🌫️', label: 'Brouillard' },
  '50n': { icon: '🌫️', label: 'Brouillard' },
};

// Ville par défaut (Vermelles - 62980)
const DEFAULT_CITY = 'Vermelles';
const DEFAULT_COORDS = { lat: 50.4833, lon: 2.7333 };

export default function WeatherWidget({ compact = false }) {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [useGeolocation, setUseGeolocation] = useState(false);
  const [locationName, setLocationName] = useState(DEFAULT_CITY);
  const { isDarkMode } = useTheme();

  const fetchWeather = async (lat, lon, cityName = null) => {
    setLoading(true);
    setError(null);
    
    try {
      // Utiliser Open-Meteo API (gratuit, pas de clé API)
      const response = await fetch(
        `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,weather_code,wind_speed_10m,wind_direction_10m&daily=temperature_2m_max,temperature_2m_min,weather_code&timezone=Europe/Paris`
      );
      
      if (!response.ok) throw new Error('Erreur météo');
      
      const data = await response.json();
      
      // Mapper les codes météo Open-Meteo vers nos icônes
      const weatherCode = data.current.weather_code;
      const iconCode = mapWeatherCode(weatherCode);
      
      setWeather({
        temp: Math.round(data.current.temperature_2m),
        humidity: data.current.relative_humidity_2m,
        wind: Math.round(data.current.wind_speed_10m),
        windDirection: data.current.wind_direction_10m || 0,
        icon: iconCode,
        daily: data.daily ? [
          { day: 'Demain', max: Math.round(data.daily.temperature_2m_max[1]), min: Math.round(data.daily.temperature_2m_min[1]), code: data.daily.weather_code[1] },
          { day: 'J+2', max: Math.round(data.daily.temperature_2m_max[2]), min: Math.round(data.daily.temperature_2m_min[2]), code: data.daily.weather_code[2] },
        ] : []
      });
      
      if (cityName) setLocationName(cityName);
    } catch (err) {
      console.error('Erreur météo:', err);
      setError('Impossible de charger la météo');
    } finally {
      setLoading(false);
    }
  };

  // Mapper les codes météo Open-Meteo vers des icônes
  const mapWeatherCode = (code) => {
    if (code === 0) return '01d';
    if (code <= 3) return '02d';
    if (code <= 48) return '03d';
    if (code <= 57) return '09d';
    if (code <= 67) return '10d';
    if (code <= 77) return '13d';
    if (code <= 82) return '09d';
    if (code <= 86) return '13d';
    if (code >= 95) return '11d';
    return '03d';
  };

  // Obtenir la direction du vent en texte
  const getWindDirection = (degrees) => {
    const directions = ['N', 'NE', 'E', 'SE', 'S', 'SO', 'O', 'NO'];
    const index = Math.round(degrees / 45) % 8;
    return directions[index];
  };

  // 🧭 Composant Rose des Vents SPATIALE
  const WindRose = ({ direction, speed }) => {
    return (
      <div className="relative w-28 h-28">
        <svg viewBox="0 0 100 100" className="w-full h-full">
          {/* Cercle de fond spatial */}
          <defs>
            <radialGradient id="spaceGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="rgba(6, 182, 212, 0.2)" />
              <stop offset="100%" stopColor="transparent" />
            </radialGradient>
          </defs>
          <circle cx="50" cy="50" r="46" fill="url(#spaceGlow)" />
          <circle cx="50" cy="50" r="45" fill="rgba(2, 6, 23, 0.8)" stroke="rgba(6, 182, 212, 0.4)" strokeWidth="1" />
          <circle cx="50" cy="50" r="35" fill="none" stroke="rgba(6, 182, 212, 0.15)" strokeWidth="1" strokeDasharray="2 4" />
          <circle cx="50" cy="50" r="25" fill="none" stroke="rgba(6, 182, 212, 0.1)" strokeWidth="1" strokeDasharray="2 4" />
          
          {/* Points cardinaux - Style HUD */}
          <text x="50" y="10" textAnchor="middle" fill="#06B6D4" fontSize="9" fontWeight="bold" fontFamily="monospace">N</text>
          <text x="94" y="54" textAnchor="middle" fill="#06B6D4" fontSize="9" fontWeight="bold" fontFamily="monospace">E</text>
          <text x="50" y="98" textAnchor="middle" fill="#06B6D4" fontSize="9" fontWeight="bold" fontFamily="monospace">S</text>
          <text x="6" y="54" textAnchor="middle" fill="#06B6D4" fontSize="9" fontWeight="bold" fontFamily="monospace">O</text>
          
          {/* Points intermédiaires */}
          <text x="79" y="24" textAnchor="middle" fill="#64748B" fontSize="6" fontFamily="monospace">NE</text>
          <text x="79" y="80" textAnchor="middle" fill="#64748B" fontSize="6" fontFamily="monospace">SE</text>
          <text x="21" y="80" textAnchor="middle" fill="#64748B" fontSize="6" fontFamily="monospace">SO</text>
          <text x="21" y="24" textAnchor="middle" fill="#64748B" fontSize="6" fontFamily="monospace">NO</text>
          
          {/* Lignes de repère */}
          <line x1="50" y1="15" x2="50" y2="22" stroke="#06B6D4" strokeWidth="2" opacity="0.6" />
          <line x1="50" y1="78" x2="50" y2="85" stroke="#06B6D4" strokeWidth="2" opacity="0.6" />
          <line x1="15" y1="50" x2="22" y2="50" stroke="#06B6D4" strokeWidth="2" opacity="0.6" />
          <line x1="78" y1="50" x2="85" y2="50" stroke="#06B6D4" strokeWidth="2" opacity="0.6" />
          
          {/* Flèche de direction du vent - Style holographique */}
          <g transform={`rotate(${direction}, 50, 50)`}>
            {/* Glow effect */}
            <polygon 
              points="50,18 43,42 50,36 57,42" 
              fill="rgba(245, 158, 11, 0.3)"
              filter="blur(2px)"
            />
            {/* Flèche principale */}
            <polygon 
              points="50,18 44,40 50,34 56,40" 
              fill="#F59E0B" 
              stroke="#FCD34D"
              strokeWidth="1"
            />
            {/* Queue de la flèche */}
            <line x1="50" y1="34" x2="50" y2="72" stroke="#F59E0B" strokeWidth="3" strokeLinecap="round" />
            <line x1="50" y1="34" x2="50" y2="72" stroke="#FCD34D" strokeWidth="1" strokeLinecap="round" />
          </g>
          
          {/* Cercle central - Core */}
          <circle cx="50" cy="50" r="10" fill="#020617" stroke="#06B6D4" strokeWidth="2" />
          <circle cx="50" cy="50" r="6" fill="rgba(6, 182, 212, 0.2)" />
        </svg>
        
        {/* Vitesse au centre */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <span className="text-xs font-bold text-cyan-400 font-mono">{speed}</span>
            <span className="text-[8px] text-gray-500 block">km/h</span>
          </div>
        </div>
      </div>
    );
  };

  const handleGeolocation = () => {
    if (!navigator.geolocation) {
      setError('Géolocalisation non disponible');
      return;
    }

    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setUseGeolocation(true);
        fetchWeather(position.coords.latitude, position.coords.longitude, 'Ma position');
      },
      (err) => {
        console.error('Erreur géoloc:', err);
        setError('Position non disponible');
        setLoading(false);
      }
    );
  };

  const handleUseDefault = () => {
    setUseGeolocation(false);
    fetchWeather(DEFAULT_COORDS.lat, DEFAULT_COORDS.lon, DEFAULT_CITY);
  };

  useEffect(() => {
    fetchWeather(DEFAULT_COORDS.lat, DEFAULT_COORDS.lon, DEFAULT_CITY);
  }, []);

  const weatherInfo = weather ? WEATHER_ICONS[weather.icon] || WEATHER_ICONS['03d'] : null;

  if (compact) {
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="flex items-center gap-3 p-3 rounded-xl border bg-slate-900/80 border-cyan-500/30"
      >
        {loading ? (
          <div className="w-8 h-8 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin" />
        ) : weather ? (
          <>
            <span className="text-3xl">{weatherInfo?.icon}</span>
            <div>
              <p className="font-bold text-lg text-white">{weather.temp}°C</p>
              <p className="text-xs text-gray-400">{locationName}</p>
            </div>
          </>
        ) : (
          <p className="text-red-500 text-sm">Météo indisponible</p>
        )}
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl h-full"
      style={{
        background: 'linear-gradient(135deg, #020617 0%, #0f172a 50%, #1e1b4b 100%)',
        boxShadow: '0 0 40px rgba(6, 182, 212, 0.2), 0 20px 40px rgba(0,0,0,0.5)',
        border: '1px solid rgba(148, 163, 184, 0.1)'
      }}
    >
      {/* 🌠 Background stars */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(15)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 rounded-full bg-white"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              opacity: Math.random() * 0.3 + 0.1
            }}
            animate={{
              opacity: [0.1, 0.5, 0.1],
              scale: [1, 1.3, 1]
            }}
            transition={{
              duration: 2 + Math.random() * 2,
              repeat: Infinity,
              delay: Math.random() * 2
            }}
          />
        ))}
      </div>

      {/* HUD corners */}
      <div className="absolute top-2 left-2 w-3 h-3 border-t-2 border-l-2 border-cyan-500/50" />
      <div className="absolute top-2 right-2 w-3 h-3 border-t-2 border-r-2 border-cyan-500/50" />
      <div className="absolute bottom-2 left-2 w-3 h-3 border-b-2 border-l-2 border-blue-500/50" />
      <div className="absolute bottom-2 right-2 w-3 h-3 border-b-2 border-r-2 border-blue-500/50" />

      {/* Header */}
      <div className="relative p-4 border-b border-white/5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-cyan-500/10 border border-cyan-500/30">
              <Cloud className="w-5 h-5 text-cyan-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                🛰️ Station Météo
              </h3>
              <p className="text-[10px] font-mono text-gray-500 tracking-wider">DONNÉES SATELLITE</p>
            </div>
          </div>
          
          <div className="flex items-center gap-1 bg-black/40 rounded-lg p-1 border border-white/5">
            <Button
              onClick={handleGeolocation}
              size="sm"
              variant="ghost"
              className={`h-8 w-8 p-0 ${useGeolocation ? 'text-cyan-400 bg-cyan-500/20' : 'text-gray-500'}`}
              title="Ma position"
            >
              <Navigation className="w-4 h-4" />
            </Button>
            <Button
              onClick={handleUseDefault}
              size="sm"
              variant="ghost"
              className={`h-8 w-8 p-0 ${!useGeolocation ? 'text-cyan-400 bg-cyan-500/20' : 'text-gray-500'}`}
              title={DEFAULT_CITY}
            >
              <MapPin className="w-4 h-4" />
            </Button>
            <Button
              onClick={() => fetchWeather(
                useGeolocation ? undefined : DEFAULT_COORDS.lat,
                useGeolocation ? undefined : DEFAULT_COORDS.lon,
                useGeolocation ? 'Ma position' : DEFAULT_CITY
              )}
              size="sm"
              variant="ghost"
              className="h-8 w-8 p-0 text-gray-500 hover:text-cyan-400"
              disabled={loading}
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            </Button>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="relative p-5">
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <motion.div 
              className="w-14 h-14 border-3 border-cyan-500/30 border-t-cyan-500 rounded-full"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
          </div>
        ) : error ? (
          <div className="text-center py-8">
            <p className="text-red-500">{error}</p>
            <Button onClick={handleUseDefault} size="sm" className="mt-2 bg-cyan-500 hover:bg-cyan-600">
              Réessayer
            </Button>
          </div>
        ) : weather ? (
          <>
            {/* Météo actuelle */}
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="flex items-center gap-2 text-sm mb-2 text-gray-400">
                  <MapPin className="w-4 h-4 text-cyan-500" />
                  <span className="font-mono tracking-wide">{locationName.toUpperCase()}</span>
                </div>
                <div className="flex items-baseline gap-1">
                  <motion.span 
                    className="text-6xl font-bold text-white"
                    style={{ textShadow: '0 0 30px rgba(6, 182, 212, 0.3)' }}
                    initial={{ scale: 0.5, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    transition={{ type: "spring" }}
                  >
                    {weather.temp}°
                  </motion.span>
                </div>
                <p className="mt-1 text-gray-400">{weatherInfo?.label}</p>
              </div>
              <motion.div
                className="text-7xl"
                animate={{ 
                  scale: [1, 1.05, 1],
                  filter: ['drop-shadow(0 0 10px rgba(255,255,255,0.1))', 'drop-shadow(0 0 20px rgba(255,255,255,0.2))', 'drop-shadow(0 0 10px rgba(255,255,255,0.1))']
                }}
                transition={{ duration: 3, repeat: Infinity }}
              >
                {weatherInfo?.icon}
              </motion.div>
            </div>

            {/* Détails - Grid spatial */}
            <div className="grid grid-cols-3 gap-3 mb-5">
              {/* Humidité */}
              <div className="p-3 rounded-xl bg-blue-500/10 border border-blue-500/20">
                <div className="flex items-center gap-1.5 text-blue-400 text-xs mb-1">
                  <Droplets className="w-3.5 h-3.5" />
                  <span className="font-mono text-[10px]">HUMIDITÉ</span>
                </div>
                <p className="font-bold text-2xl text-white">{weather.humidity}<span className="text-sm text-gray-400">%</span></p>
              </div>
              
              {/* Vent */}
              <div className="p-3 rounded-xl bg-cyan-500/10 border border-cyan-500/20">
                <div className="flex items-center gap-1.5 text-cyan-400 text-xs mb-1">
                  <Wind className="w-3.5 h-3.5" />
                  <span className="font-mono text-[10px]">VENT</span>
                </div>
                <p className="font-bold text-2xl text-white">{weather.wind}<span className="text-sm text-gray-400">km/h</span></p>
                <p className="text-cyan-400 text-xs font-mono">{getWindDirection(weather.windDirection)}</p>
              </div>
              
              {/* Rose des vents */}
              <div className="flex items-center justify-center">
                <WindRose direction={weather.windDirection} speed={weather.wind} />
              </div>
            </div>

            {/* Prévisions */}
            {weather.daily && weather.daily.length > 0 && (
              <div className="border-t border-white/5 pt-4">
                <p className="text-xs text-gray-500 mb-3 font-mono tracking-wider">PRÉVISIONS ORBITALES</p>
                <div className="flex gap-3">
                  {weather.daily.map((day, idx) => (
                    <motion.div 
                      key={idx} 
                      className="flex-1 text-center p-3 rounded-xl bg-white/5 border border-white/5"
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: idx * 0.1 }}
                    >
                      <p className="text-[10px] mb-2 text-gray-500 font-mono">{day.day.toUpperCase()}</p>
                      <p className="text-2xl mb-1">{WEATHER_ICONS[mapWeatherCode(day.code)]?.icon || '☁️'}</p>
                      <p className="text-sm">
                        <span className="text-cyan-400 font-bold">{day.max}°</span>
                        <span className="mx-1 text-gray-600">/</span>
                        <span className="text-gray-500">{day.min}°</span>
                      </p>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : null}
      </div>

      {/* Footer HUD */}
      <div className="px-4 py-2 border-t border-white/5 flex items-center justify-between">
        <span className="text-[9px] font-mono text-gray-600 tracking-widest">WEATHER DECK v2.0</span>
        <div className="flex gap-1">
          <div className="w-1 h-1 rounded-full bg-cyan-500" />
          <div className="w-1 h-1 rounded-full bg-blue-500" />
          <div className="w-1 h-1 rounded-full bg-purple-500" />
        </div>
      </div>
    </motion.div>
  );
}
